import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Keyboard, SelectExtendedProps } from 'grommet';
import { observer } from 'mobx-react-lite';
import { ClientService } from '/src/api';
import { BoxProps, Select } from '/src/components';
import { useGlobalStore, useUserStore } from '/src/context';
import { TClientId } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { SelectOption } from '/src/lib/types';

export const ClientsSelect: React.FC<ClientsSelectProps> = observer((props) => {
  const { user: currentUser, isFuseAdmin } = useUserStore();
  const { handleApiError } = useGlobalStore();
  const { t } = useTranslation();
  const {
    id,
    name = 'selected_client',
    selectedClientId,
    setSelectedClientId,
    options,
    error,
    required,
    hideOptionalText,
    disabled,
    label,
    placeholder,
    searchPlaceholder = 'Filter by Company Name...',
    onSubmit,
    excludeClientIds,
    multiple,
    isLoadingOptions,
    selectedClientIds,
    setSelectedClientIds,
    fill,
  } = props;

  const [fetchedOptions, setFetchedOptions] = useState<SelectOption<TClientId | undefined>[]>([]);

  useEffect(() => {
    if (options) return;

    fetchClientsList().catch((err) => handleApiError(err, toastMessages.listClients.error));
  }, [options]);

  const fetchClientsList = async () => {
    if (currentUser) {
      if (isFuseAdmin) {
        const data = await ClientService.listClientNames();
        const options = data.map((client) => ({
          label: client.name,
          value: client.id,
        }));
        setFetchedOptions(options);
      } else {
        const options = currentUser.clients.map((client) => ({
          label: client.name,
          value: client.id,
        }));
        setFetchedOptions(options);
      }
    }
  };

  const baseOptions = options ?? fetchedOptions;
  const filteredOptions = excludeClientIds
    ? baseOptions.filter((option) => !excludeClientIds.includes(option.value))
    : baseOptions;

  return (
    <Keyboard onEnter={onSubmit}>
      <Select
        multiple={multiple}
        isLoadingOptions={isLoadingOptions}
        id={id}
        name={name}
        label={label ?? t('user:company')}
        value={selectedClientId}
        setValue={(id) => setSelectedClientId?.(id)}
        values={selectedClientIds ?? []}
        setValues={(values) => setSelectedClientIds?.(values as TClientId[])}
        options={filteredOptions}
        required={required}
        disabled={disabled}
        error={error}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        hideOptionalText={hideOptionalText}
        fill={fill}
      />
    </Keyboard>
  );
});

export type ClientsSelectProps = {
  selectedClientId?: number;
  setSelectedClientId?: (index: number) => void;
  multiple?: boolean;
  isLoadingOptions?: boolean;
  selectedClientIds?: TClientId[];
  setSelectedClientIds?: (ids: TClientId[]) => void;
  options?: { label: string; value: number | undefined }[];
  error?: string;
  required?: boolean;
  hideOptionalText?: boolean;
  style?: SelectExtendedProps['style'];
  label?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  id?: string;
  // Name must be unique per instance in a grommet Form, or the form loops.
  name?: string;
  disabled?: boolean;
  onSubmit?: (event: React.KeyboardEvent<HTMLElement>) => void;
  excludeClientIds?: (number | undefined)[];
  fill?: BoxProps['fill'];
};
